import React, { useState, useEffect, useMemo } from "react";
import CustomTable from "../CustomTable";
import snakeToTitleCase from "../utils/SnakeToTitleCase";
import { capitalize } from "lodash";
import { Button } from "react-bootstrap";
import PolicyAnnouncementFormModal from "./PolicyAnnouncementFormModal";
import Modal from "../Modal";

const READ_ONLY_FORM_ACTIONS = ["view", "review", "submit", "publish", "revert", "archive"];

const PolicyAnnouncementsTable = (props) => {
  const [showPolicyAnnouncementModal, setShowPolicyAnnouncementModal] = useState(false);
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);
  const [displayAnnouncement, setDisplayAnnouncement] = useState(null);
  const [formAction, setFormAction] = useState("create");
  const [confirmationMessage, setConfirmationMessage] = useState("");

  const readOnly = useMemo(() => READ_ONLY_FORM_ACTIONS.includes(formAction), [formAction]);
  const espUrl = props.envESPAppDomain;

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "draft":
        return "warning";
      case "ready_for_review":
        return "primary";
      case "approved":
        return "success";
      case "active":
        return "light text-dark";
      default:
        return "secondary";
    }
  };

  const actionButtons = [
    {
      key: "view",
      label: "View",
      confirmationMessage: "This modal is view-only. Changes cannot be made.",
      className: "btn-xs btn-secondary my-1 ms-2",
      disabled: false,
      validStatuses: [
        "active",
        "draft",
        "ready_for_review",
        "approved",
        "reverted",
        "archived",
        "retired",
      ],
      validTabs: ["all", "review", "active"],
      display: props.canView,
    },
    {
      key: "update",
      label: "Edit",
      className: "btn-xs btn-warning my-1 ms-2",
      disabled: false,
      validStatuses: ["draft"],
      validTabs: ["all"],
      display: props.canUpdate,
    },
    {
      key: "submit",
      label: "Submit for Review",
      className: "btn-xs btn-success my-1 ms-2 text-nowrap",
      disabled: false,
      confirmationMessage:
        "Are you ready to submit this policy form for review? Once submitted, you will not be able to make any changes unless the approver rejects the form.",
      validStatuses: ["draft"],
      validTabs: ["all"],
      display: props.canSubmit,
    },
    {
      key: "review",
      label: "Review",
      className: "btn-xs btn-warning my-1 ms-2 text-nowrap",
      disabled: false,
      validStatuses: ["ready_for_review"],
      validTabs: ["all", "review"],
      display: props.canReview,
    },
    {
      key: "publish",
      label: "Publish",
      className: "btn-xs btn-success my-1 ms-2",
      confirmationMessage: `Are you sure you want to publish this policy form? By clicking publish, this policy form and its contents will immediately be live on ${espUrl}. Please double check the content before clicking publish.`,
      validStatuses: ["approved"],
      validTabs: ["all", "review"],
      display: props.canPublish,
    },
    {
      key: "archive",
      label: "Archive",
      className: "btn-xs btn-danger my-1 ms-2",
      confirmationMessage:
        "Are you sure you want to archive this policy form? You will not be able to undo this action.",
      disabled: false,
      validStatuses: ["draft", "ready_for_review", "approved"],
      validTabs: ["all", "review", "active"],
      display: props.canArchive,
    },
    {
      key: "revert",
      label: "Revert",
      confirmationMessage: `Are you sure you want to revert this active policy form? By clicking 'Revert' the current version will be removed from ${espUrl} and the previous version will be restored.`,
      className: "btn-xs btn-danger my-1 ms-2",
      disabled: false,
      validStatuses: ["active"],
      validTabs: ["all", "active"],
      display: props.canRevert,
    },
  ];

  const renderAnnouncementActions = (tableRow) => {
    return (
      <div className="d-flex flex-column">
        {actionButtons
          .filter((button) => {
            const matchesBasicRules =
              Boolean(button.display) &&
              button.validStatuses.includes(tableRow.status) &&
              button.validTabs.includes(props.tab);
            if (!matchesBasicRules) return false;

            const hasPreviousAnnouncement = !!tableRow.previous_policy_announcement;
            if (button.key === "revert") return hasPreviousAnnouncement;

            return true;
          })
          .map((button) => {
            const { validStatuses, validTabs, confirmationMessage, display, ...btnProps } = button;
            return (
              <Button
                key={button.key}
                onClick={() => {
                  const isRevert = button.key === "revert";

                  const message = isRevert
                    ? `Are you sure you want to revert to the previous version? Please review the content below from the previous version for ${tableRow.manufacturer} ${snakeToTitleCase(tableRow.policy_type)} policy announcement.`
                    : button.confirmationMessage || "";

                  const displayAnnouncement = isRevert
                    ? tableRow.previous_policy_announcement
                    : tableRow;

                  setConfirmationMessage(message);
                  setFormAction(button.key);
                  setActiveAnnouncement(tableRow);
                  setDisplayAnnouncement(displayAnnouncement);
                  setShowPolicyAnnouncementModal(true);
                }}
                {...btnProps}
              >
                {button.label}
              </Button>
            );
          })}
      </div>
    );
  };

  const availableColumns = useMemo(
    () => [
      {
        Header: "Manufacturer",
        accessor: "manufacturer",
      },
      {
        Header: "Policy Type",
        accessor: "policy_type",
        Cell: (tableProps) => {
          return (
            <small className="badge bg-secondary text-uppercase">
              {snakeToTitleCase(tableProps.row.original.policy_type)}
            </small>
          );
        },
      },
      {
        Header: "PDF",
        accessor: "pdf",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (tableProps) => {
          return (
            <small
              className={`badge bg-${getStatusBadgeClass(tableProps.row.original.status)} text-uppercase`}
            >
              {snakeToTitleCase(tableProps.row.original.status)}
            </small>
          );
        },
      },
      {
        Header: "Last Action",
        accessor: "last_action",
      },
      {
        Header: "Last Action Performed By",
        Cell: (tableProps) => {
          return (
            <>
              <b>{tableProps.row.original.last_action_by}</b>
              <br />
              <small className="text-muted">{tableProps.row.original.last_action_at}</small>
            </>
          );
        },
      },
      {
        Header: "Published Date",
        accessor: "published_date",
        Cell: (tableProps) => <small>{tableProps.row.original.published_date ?? "--"}</small>,
      },
      {
        Header: "Target Announcement Date",
        accessor: "target_launch_date",
      },
      {
        Header: "Uploaded By",
        Cell: (tableProps) => (
          <>
            <b>{tableProps.row.original.uploaded_by}</b>
            <br />
            <small className="text-muted">{tableProps.row.original.uploaded_date}</small>
          </>
        ),
      },
      {
        Header: "Approved By",
        Cell: (tableProps) => (
          <>
            <b>{tableProps.row.original.approved_by}</b>
            <br />
            <small className="text-muted">{tableProps.row.original.approved_date}</small>
          </>
        ),
      },
      {
        Header: "Published By",
        Cell: (tableProps) => (
          <>
            <b>{tableProps.row.original.published_by}</b>
            <br />
            <small className="text-muted">{tableProps.row.original.published_date}</small>
          </>
        ),
      },
      {
        Header: "Actions",
        Cell: (tableProps) => {
          return renderAnnouncementActions(tableProps.row.original);
        },
      },
    ],
    []
  );

  const columns = useMemo(
    () => props.columns.map((c) => availableColumns.find((ac) => ac.Header === c)),
    [props.columns]
  );

  const dismissModal = () => setShowPolicyAnnouncementModal(false);

  return (
    <>
      <CustomTable columns={columns} data={props.policyAnnouncements} />
      <Modal
        show={showPolicyAnnouncementModal}
        title={`${capitalize(formAction)} Policy Announcement`}
        buttonText="Update Policy Announcement"
        buttonType="success"
        modalId={`${formAction}_announcement_modal`}
        disableFooter={true}
        body={
          <PolicyAnnouncementFormModal
            manufacturers={props.manufacturers}
            policyTypes={props.policyTypes}
            action={formAction}
            activeAnnouncement={activeAnnouncement ?? {}}
            displayAnnouncement={displayAnnouncement ?? {}}
            onCloseModal={dismissModal}
            readOnly={readOnly}
            confirmationMessage={confirmationMessage}
          />
        }
        dismiss={dismissModal}
      />
    </>
  );
};

export default PolicyAnnouncementsTable;
